import { useContext, useState } from 'react';
import { Box, Snackbar, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButtonAccept,
    SttDialog,
    SttFormText,
} from '../../../../sporttia/all';
import useUsersService from '../../../../services/UsersService';
import useGameService from '../../../../services/GameService';
import type { Error as ApiError } from '../../../../types/models/Error';

interface SignUpDialogProps {
    open: boolean;
    gameId: number;
    onClose: () => void;
    onEmailSent: () => void;
}

export default function SignUpDialog({
    open,
    gameId,
    onClose,
    onEmailSent,
}: SignUpDialogProps) {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const navigate = useNavigate();

    const cxt = useContext(AppContext)!;

    const { useValidateUser } = useUsersService();
    const { usePostGameSignUp } = useGameService();

    const { mutate: validateUser, isPending: isLoadingValidate } =
        useValidateUser();
    const { mutate: postGameSignUp, isPending: isLoadingSignUp } =
        usePostGameSignUp(gameId);

    const handleEmailSubmit = () => {
        setErrorMessage('');
        setSuccessMsg('');

        validateUser(
            { params: { field: 'email', value: email } },
            {
                onSuccess: ({ exists }: any) => {
                    if (exists) {
                        postGameSignUp(
                            { params: { email } },
                            {
                                onSuccess: ({ sent }: any) => {
                                    if (sent) {
                                        onEmailSent();
                                        onClose();
                                        return setSuccessMsg(
                                            t('emailLinkSent'),
                                        );
                                    }
                                    setErrorMessage(t('errorSendingEmail'));
                                },
                                onError: (err: unknown) => {
                                    const apiErr = err as ApiError;
                                    const errorMsg =
                                        apiErr?.error?.msg ||
                                        t('errorSendingEmail');
                                    cxt.showMessage('E', errorMsg);
                                },
                            },
                        );
                    } else {
                        navigate(
                            `/games/${gameId}/register?email=${encodeURIComponent(email)}`,
                        );
                    }
                },
                onError: (err: unknown) => {
                    const apiErr = err as ApiError;
                    const errorMsg =
                        apiErr?.error?.msg || t('errorSendingEmail');
                    cxt.showMessage('E', errorMsg);
                },
            },
        );
    };

    return (
        <>
            <Snackbar
                open={successMsg.length > 0}
                autoHideDuration={6000}
                message={successMsg}
                onClose={() => setSuccessMsg('')}
            />

            <SttDialog
                open={open}
                onClose={onClose}
                title={t('matchRegistration')}
                content={
                    <Box p={2}>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            align="center"
                            style={{ marginBottom: 2 }}
                        >
                            {t('enterEmailToJoin')}
                        </Typography>
                        <SttFormText
                            name="email"
                            caption={t('Email')}
                            defVal={email}
                            onChange={({ value }: any) => setEmail(value)}
                            type="email"
                            required
                            disabled={isLoadingValidate || isLoadingSignUp}
                        />
                        {errorMessage && (
                            <Typography
                                color="error"
                                align="center"
                                style={{ fontWeight: 600 }}
                            >
                                {errorMessage}
                            </Typography>
                        )}
                    </Box>
                }
                buttons={[
                    {
                        type: 'component',
                        component: (
                            <SttButtonAccept
                                fullWidth
                                onClick={handleEmailSubmit}
                                disabled={isLoadingValidate || isLoadingSignUp}
                            />
                        ),
                    },
                ]}
            />
        </>
    );
}
